import { useState } from 'react';
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Typography,
  List,
  ListItem,
  Chip,
  SwipeableDrawer,
  IconButton,
  TextField,
  InputAdornment,
  Skeleton,
  Badge,
  useTheme,
  Divider,
  ListItemButton,
} from '@mui/material';
import {
  List as ListIcon,
  Search,
  Close,
  Tune,
  ChevronRight,
  AccessTime,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { CaseResponseDto } from '@bandido/shared/dtos';
import { addLeadingZeros } from '@frontend/features/common/utils/helpers';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { Link } from '@frontend/features/common/components/Link';
import { routes } from '@frontend/router/routes';
import { getTimeSinceDate } from '@frontend/features/common/helper';

interface CasesListMobileProps {
  title: string;
  subtitle: string;
  pagination: any;
  filtersCount: number;
  filtersOptions: any;
  onSearchChange: (value: string) => void;
  sorting: any;
  onCreate: () => void;
  actionToggles?: any[];
  statisticsButton: JSX.Element;
  isLoading: boolean;
  items: CaseResponseDto[];
  totalCount: number;
  isStatisticsModalOpen: boolean;
  onCloseStatistics: () => void;
}

export const CasesListMobile = ({
  filtersCount,
  filtersOptions,
  onSearchChange,
  isLoading,
  items,
}: CasesListMobileProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [bottomNavValue, setBottomNavValue] = useState(0);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleBottomNavChange = (_: any, newValue: number) => {
    setBottomNavValue(newValue);

    if (newValue === 1) {
      // Filters tab
      setFiltersDrawerOpen(true);
    } else if (newValue === 2) {
      // Search tab
      setSearchDrawerOpen(true);
    }
  };

  const handleSearchSubmit = () => {
    onSearchChange(searchValue);
    setSearchDrawerOpen(false);
    setBottomNavValue(0); // Return to list
  };

  const renderCaseListItem = (caseItem: CaseResponseDto, index: number) => {
    const clientName = caseItem.clientFirstName
      ? `${caseItem.clientFirstName} ${caseItem.clientLastName}`
      : 'N/A';

    const agentName = caseItem.agentId
      ? `${caseItem.agentFirstName} ${caseItem.agentLastName}`
      : t('common.unassigned');

    return (
      <Box key={caseItem.id}>
        <ListItemButton
          component={Link}
          to={routes.dashboard.caseView.replace(':id', String(caseItem.id))}
          sx={{
            py: 2,
            px: 2,
            flexDirection: 'column',
            gap: 2,
            alignItems: 'stretch',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            '&:active': {
              backgroundColor: theme.palette.action.selected,
            }
          }}
        >
          <Box>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" >
                <Typography variant="body1" >
                  {caseItem.name}
                </Typography>
                <ChevronRight sx={{ color: theme.palette.action.disabled }} />
              </Box>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" >
              <Typography variant="subtitle2">
                Caso #{addLeadingZeros(Number(caseItem.id), defaultLeadingZeros)}
              </Typography>
              <Typography variant="caption" color="text.primary">
                {clientName}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>

            <Box display="flex" justifyContent="space-between" alignItems="center" >
              <Typography variant='caption' color="text.secondary">{t('common.status')}</Typography>
              <Chip
                size="small"
                label={t(`cases.status.${caseItem.status}`)}
                sx={{
                  backgroundColor: theme.palette.cases[caseItem.status].bg,
                  color: theme.palette.cases[caseItem.status].text,

                }}
              />
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" >
              <Typography variant='caption' color="text.secondary">{t('common.step')}</Typography>
              <Typography variant='caption' color="text.primary">{caseItem.stepName || t('common.unassigned')}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" >
              <Typography variant='caption' color="text.secondary">{t('common.workflow')}</Typography>
              <Typography variant='caption'>{caseItem.workflowName || t('common.unassigned')}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" >
              <Typography variant='caption' color="text.secondary">{t('common.channel')}</Typography>
              <Typography variant='caption' color="text.primary">{caseItem.originatedByChannel || t('common.unassigned')}</Typography>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="caption" color="text.secondary">{t('common.agent')}</Typography>
              <Typography variant="caption" color="text.primary">{agentName}</Typography>
            </Box>

            {/* Last Update */}
            <Box display="flex" flexDirection="row-reverse" alignItems="center" gap={0.5} mt={1}>
              <AccessTime sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
              <Typography variant="caption" color="text.secondary">
                {t('common.updated')} {getTimeSinceDate(caseItem.updatedAt)}
              </Typography>
            </Box>
          </Box>
        </ListItemButton>

        {index < items.length - 1 && <Divider />}
      </Box>
    );
  };

  return (
    <Box sx={{ pb: 8 }}>
      {/* Cases List */}
      <List sx={{ p: 0 }}>
        {isLoading ? (
          // Loading skeletons
          Array.from({ length: 5 }).map((_, index) => (
            <Box key={index}>
              <ListItem sx={{ py: 2, px: 2, flexDirection: 'column', alignItems: 'stretch' }}>
                <Skeleton variant="text" width="40%" height={28} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="60%" height={16} />
              </ListItem>
              {index < 4 && <Divider />}
            </Box>
          ))
        ) : (
          // Cases list
          items.map((item, index) => renderCaseListItem(item, index))
        )}
      </List>

      {/* Filters Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={filtersDrawerOpen}
        onClose={() => setFiltersDrawerOpen(false)}
        onOpen={() => setFiltersDrawerOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            height: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              {t('common.filters')}
            </Typography>
            <IconButton onClick={() => setFiltersDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          {filtersOptions?.form}
        </Box>
      </SwipeableDrawer>

      {/* Search Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={searchDrawerOpen}
        onClose={() => setSearchDrawerOpen(false)}
        onOpen={() => setSearchDrawerOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            height: '60vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight={600}>
              {t('common.search')}
            </Typography>
            <IconButton onClick={() => setSearchDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            placeholder={t('common.search')}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearchSubmit();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box display="flex" gap={1} justifyContent="flex-end">
            <IconButton
              onClick={() => setSearchDrawerOpen(false)}
              sx={{
                bgcolor: theme.palette.grey[100],
                '&:hover': { bgcolor: theme.palette.grey[200] }
              }}
            >
              <Close />
            </IconButton>
            <IconButton
              onClick={handleSearchSubmit}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: 'white',
                '&:hover': { bgcolor: theme.palette.primary.dark }
              }}
            >
              <Search />
            </IconButton>
          </Box>
        </Box>
      </SwipeableDrawer>

      {/* Bottom Navigation */}
      <BottomNavigation
        value={bottomNavValue}
        onChange={handleBottomNavChange}
        showLabels
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
          zIndex: 1000,
          height: 64,
          backgroundColor: theme.palette.background.paper,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 12px 8px',
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.75rem',
            '&.Mui-selected': {
              fontSize: '0.75rem',
            },
          },
        }}
      >
        <BottomNavigationAction
          label={t('common.list')}
          icon={<ListIcon />}
        />
        <BottomNavigationAction
          label={t('common.filters')}
          icon={
            <Badge
              badgeContent={filtersCount > 0 ? filtersCount : undefined}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  height: 16,
                  minWidth: 16,
                }
              }}
            >
              <Tune />
            </Badge>
          }
        />
        <BottomNavigationAction
          label={t('common.search')}
          icon={<Search />}
        />
      </BottomNavigation>
    </Box>
  );
};